import { useEffect } from "react"

const INTERACTIVE = "button, a, input, select, textarea, label, [role=button], [contenteditable]"
const DRAG_THRESHOLD_PX = 4

// Lets the operator grab the bracket and drag it: horizontal movement scrolls the
// viewport, vertical movement scrolls the page. Clicks on cards, buttons and links
// keep working; a click is only swallowed after a real drag so the card under the
// pointer does not open its editor when the operator lets go.
export default function useDragPan(viewportRef, enabled) {
  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport || !enabled) return undefined
    let drag = null
    const swallowClick = event => {
      event.stopPropagation()
      event.preventDefault()
      viewport.removeEventListener("click", swallowClick, true)
    }
    const down = event => {
      if (event.button !== 0 || event.target.closest(INTERACTIVE)) return
      drag = { x: event.clientX, y: event.clientY, moved: false, pointerId: event.pointerId }
    }
    const move = event => {
      if (!drag || event.pointerId !== drag.pointerId) return
      const dx = event.clientX - drag.x
      const dy = event.clientY - drag.y
      if (!drag.moved) {
        if (Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) return
        drag.moved = true
        viewport.classList.add("is-panning")
        viewport.setPointerCapture?.(event.pointerId)
        viewport.addEventListener("click", swallowClick, true)
      }
      viewport.scrollLeft -= dx
      window.scrollBy(0, -dy)
      drag.x = event.clientX
      drag.y = event.clientY
      event.preventDefault()
    }
    const up = event => {
      if (!drag || event.pointerId !== drag.pointerId) return
      if (drag.moved) {
        viewport.classList.remove("is-panning")
        viewport.releasePointerCapture?.(event.pointerId)
        // A click that follows a drag lands synchronously after pointerup; drop the
        // listener afterwards so the next genuine click is not swallowed.
        setTimeout(() => viewport.removeEventListener("click", swallowClick, true), 0)
      }
      drag = null
    }
    viewport.addEventListener("pointerdown", down)
    viewport.addEventListener("pointermove", move)
    viewport.addEventListener("pointerup", up)
    viewport.addEventListener("pointercancel", up)
    return () => {
      viewport.classList.remove("is-panning")
      viewport.removeEventListener("pointerdown", down)
      viewport.removeEventListener("pointermove", move)
      viewport.removeEventListener("pointerup", up)
      viewport.removeEventListener("pointercancel", up)
      viewport.removeEventListener("click", swallowClick, true)
    }
  }, [viewportRef, enabled])
}
